<script>
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import EventsItem from './EventsItem.vue';
import Breadcrumbs from './util/Breadcrumbs.vue';

export default {
  setup() {
    const i18n = useI18n();
    return {
      t: i18n.t,
      locale: i18n.locale
    };
  },
  data() {
    return {
      createdEvents: [],
      participatedEvents: [],
      isLoading: false,
      isLoadingCreated: false,
      isLoadingParticipated: false,
      userPermissions: null,
      isLoadingPermissions: false,
    }
  },
  components: {
    'events-item': EventsItem,
    'breadcrumbs': Breadcrumbs,
  },
  methods: {
    getCreatedEvents() {
      this.isLoadingCreated = true;
      const url = "/api/v1/events";
      const params = {
        filter: {
          user_id: 'current' // Special filter for current user's created events
        }
      };

      axios.get(url, { params: params })
        .then(response => {
          // Ensure we always store an Array to avoid `.length` crashes in templates
          const payload = response?.data;
          const list = payload?.data ?? payload;
          this.createdEvents = Array.isArray(list) ? list : [];
        })
        .catch(error => {
          console.log('Error fetching created events:', error);
          this.createdEvents = [];
        })
        .finally(() => {
          this.isLoadingCreated = false;
        });
    },

    getParticipatedEvents() {
      this.isLoadingParticipated = true;
      const url = "/api/v1/events";
      const params = {
        filter: {
          participant_id: 'current' // Special filter for current user's participated events
        }
      };

      axios.get(url, { params: params })
        .then(response => {
          // Ensure we always store an Array to avoid `.length` crashes in templates
          const payload = response?.data;
          const list = payload?.data ?? payload;
          this.participatedEvents = Array.isArray(list) ? list : [];
        })
        .catch(error => {
          console.log('Error fetching participated events:', error);
          this.participatedEvents = [];
        })
        .finally(() => {
          this.isLoadingParticipated = false;
        });
    },

    join(event) {
      const url = "/api/v1/events/join";
      axios.post(url, { event_id: event.id } )
        .then(response => {
          event.attributes.joinable = false;
          event.attributes.joined = true;
        })
        .catch(error => {
          console.log(error);
        });
    },

    handleEventUpdated(updatedEvent) {
      // Find and update the event in both created and participated events arrays
      const createdIndex = this.createdEvents.findIndex(event => event.id === updatedEvent.id);
      if (createdIndex !== -1) {
        this.createdEvents.splice(createdIndex, 1, updatedEvent);
      }

      const participatedIndex = this.participatedEvents.findIndex(event => event.id === updatedEvent.id);
      if (participatedIndex !== -1) {
        this.participatedEvents.splice(participatedIndex, 1, updatedEvent);
      }
    },

        getUserPermissions() {
      this.isLoadingPermissions = true;
      const url = "/api/v1/events/permissions";

      axios.get(url)
        .then(response => {
          this.userPermissions = response.data;
        })
        .catch(error => {
          console.log('Error fetching user permissions:', error);
          this.userPermissions = { can_create: false, can_manage: false };
        })
        .finally(() => {
          this.isLoadingPermissions = false;
        });
    },
  },
  computed: {
    hasCreatedEvents() {
      return this.createdEvents.length > 0;
    },
    hasParticipatedEvents() {
      return this.participatedEvents.length > 0;
    }
  },
  beforeMount() {
    this.getUserPermissions();
    this.getCreatedEvents();
    this.getParticipatedEvents();
  }
}
</script>

<template>
  <breadcrumbs />

  <div class="my-events-container">
    <h2>{{ t('events.my_events.title') }}</h2>

    <div class="events-sections" :class="{ 'single-section': !userPermissions?.can_create }">
      <!-- Created Events Section -->
      <div v-if="userPermissions?.can_create" class="events-section">
        <h3>{{ t('events.my_events.created_by_me') }}</h3>

        <div v-if="isLoadingCreated" class="loading-container">
          <div class="loading-spinner">⏳</div>
          <p>{{ t('events.my_events.loading_created') }}</p>
        </div>

        <div v-else-if="!hasCreatedEvents" class="no-events">
          <p>{{ t('events.my_events.no_created') }}</p>
          <router-link :to="{ name: 'new_event' }" class="btn btn-primary" data-turbolinks="false">
            {{ t('events.create_event') }}
          </router-link>
        </div>

        <template v-else>
          <div class="events-count">
            {{ t('events.my_events.created_count') }} {{ createdEvents.length }}
          </div>
          <template v-for="event in createdEvents" :key="event.id">
            <events-item :event="event" @join="join" @event-updated="handleEventUpdated"></events-item>
          </template>
        </template>
      </div>

      <!-- Participated Events Section -->
      <div class="events-section">
        <h3>{{ t('events.my_events.participating') }}</h3>

        <div v-if="isLoadingParticipated" class="loading-container">
          <div class="loading-spinner">⏳</div>
          <p>{{ t('events.my_events.loading_participated') }}</p>
        </div>

        <div v-else-if="!hasParticipatedEvents" class="no-events">
          <p>{{ t('events.my_events.no_participated') }}</p>
          <router-link :to="{ name: 'list' }" class="btn btn-primary">
            {{ t('events.my_events.find_events') }}
          </router-link>
        </div>

        <template v-else>
          <div class="events-count">
            {{ t('events.my_events.participated_count') }} {{ participatedEvents.length }}
          </div>
          <template v-for="event in participatedEvents" :key="event.id">
            <events-item :event="event" @join="join" @event-updated="handleEventUpdated"></events-item>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Color Variables
$primary-bg: #efd8b0;
$primary-text: #bd9c65;
$icon-bg: #c29c58;
$icon-text: #60451f;
$hover-bg: #b08f4f;
$border-color: #bd9c65;
$focus-shadow: rgba(189, 156, 101, 0.2);

.my-events-container {
  padding: 20px 0;
}

.my-events-container h2 {
  color: $icon-text;
  margin-bottom: 30px;
  font-size: 24px;
  font-weight: bold;
}

.events-sections {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;

  &.single-section {
    grid-template-columns: 1fr;
  }
}

.events-section {
  background-color: white;
  border: 1px solid $border-color;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.events-section h3 {
  color: $icon-text;
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: bold;
  padding-bottom: 10px;
  border-bottom: 2px solid $primary-bg;
}

.loading-container {
  text-align: center;
  padding: 40px 20px;
  color: $primary-text;
}

.loading-spinner {
  font-size: 24px;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.no-events {
  text-align: center;
  padding: 40px 20px;
  color: $primary-text;
}

.no-events p {
  margin-bottom: 20px;
  font-size: 16px;
}

.btn {
  display: inline-block;
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background-color: $icon-bg;
  color: $icon-text;
}

.btn-primary:hover {
  background-color: $hover-bg;
  color: white;
}

.events-count {
  background-color: $primary-bg;
  border: 1px solid $border-color;
  border-radius: 4px;
  padding: 10px 15px;
  margin-bottom: 15px;
  color: $icon-text;
  font-size: 14px;
  font-weight: bold;
}

@media (max-width: 768px) {
  .events-sections {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .events-section {
    padding: 15px;
  }
}
</style>
